import { PrimeMark } from '@/components/PrimeMark'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Archive,
  BarChart3,
  ExternalLink,
  GitPullRequest,
  MessageSquare,
  Octagon,
  RotateCcw,
  Square,
  X,
} from 'lucide-react'
import { QUICK_CHAT } from '@/lib/lead'
import type { ReactNode } from 'react'
import { api, mutate } from '@/runtime'
import { useAgentSessions, useAllRuns } from '@/db/hooks'
import { useScope } from '@/state/scope'
import { useUi } from '@/state/ui'
import type { AgentRunResource } from '@/kinds/agentrun'
import { NewAgentSheet, isLead, prUrlOf } from '@/kinds/agentrun/components'
import { isOver } from '@/kinds/agentrun/schema'
import { Sheet } from '@/components/ui'

// Everything you can do to an agent, in one place: the ⋯ on its row in the
// Agents screen and in its thread's header. Big rows, thumb-sized, so a phone
// never needs a precise tap; ending a session asks twice.

const Item = ({
  icon,
  label,
  hint,
  onClick,
  tone,
  testid,
}: {
  icon: ReactNode
  label: string
  hint?: string
  onClick: () => void
  tone?: 'warn'
  testid?: string
}) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 min-h-14 text-left rounded-[var(--radius-card-inner)] hover:bg-raised"
      data-testid={testid}
    >
      <span className={tone === 'warn' ? 'text-rose-300' : 'text-muted'}>{icon}</span>
      <span className="flex-1 min-w-0">
        <span className={tone === 'warn' ? 'block text-[15px] text-rose-300' : 'block text-[15px]'}>
          {label}
        </span>
        {hint && <span className="block text-xs text-faint">{hint}</span>}
      </span>
    </button>
  </li>
)

export const AgentMenu = ({
  run,
  open,
  onClose,
  working,
}: {
  run: AgentRunResource
  open: boolean
  onClose: () => void
  /** whether a turn is going (Stop is offered) */
  working?: boolean
}) => {
  const navigate = useNavigate()
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [confirmFresh, setConfirmFresh] = useState(false)
  const [again, setAgain] = useState(false)
  const fresh = async () => {
    onClose()
    setConfirmFresh(false)
    try {
      await api.leadFresh()
      flash('Prime is filing away; a fresh one starts in a few minutes')
      void navigate({ to: '/agents/all' })
    } catch (e) {
      flash(navigator.onLine ? (e as Error).message : 'You are offline')
    }
  }
  const scope = useScope((s) => s.ws)
  const prime = useAgentSessions().find((s) => s.spec.prime && !s.deleted_at)
  const allRuns = useAllRuns() ?? []
  const lead = isLead(run)
  const over = isOver(run.spec.status)
  const flash = (m: string) => useUi.getState().showFlash(m, 3000)
  const go = (to: string, params?: Record<string, string>) => {
    onClose()
    void navigate({ to, params } as never)
  }
  const act = async (action: string, done: string) => {
    onClose()
    try {
      await mutate.action(run.id, action, {})
      flash(done)
    } catch (e) {
      flash((e as Error).message)
    }
  }
  const askPrime = async () => {
    onClose()
    try {
      const sess = prime?.labels.ws === scope ? prime.id : undefined
      const { session } = await api.factoryReview(run.id, run.spec.started_by ?? sess)
      // Prime on Claude Code is an agent session; the quick chat is a chat thread
      if (allRuns.some((r) => r.id === session)) {
        void navigate({ to: '/agents/run/$id', params: { id: session } })
        return
      }
      void navigate({
        to: session === run.spec.started_by ? '/agents/run/$id' : '/agents/$id',
        params: { id: session },
      } as never)
    } catch (e) {
      flash(navigator.onLine ? (e as Error).message : 'You are offline')
    }
  }
  return (
    <Sheet
      open={open}
      onClose={() => {
        setConfirmEnd(false)
        setConfirmFresh(false)
        onClose()
      }}
      title={lead ? 'Prime' : run.spec.title || run.spec.name || 'Agent'}
    >
      <ul className="flex flex-col gap-0.5 pb-2" data-testid="agent-menu">
        <Item
          icon={<MessageSquare size={18} />}
          label="Open the thread"
          onClick={() => go('/agents/run/$id', { id: run.id })}
        />
        {!lead && run.spec.pr_number ? (
          <Item
            icon={<GitPullRequest size={18} />}
            label={`Review pull request #${run.spec.pr_number}`}
            hint="Files by layer, the diff, approve or merge"
            onClick={() => go('/dev/review/$id', { id: run.id })}
            testid="menu-review"
          />
        ) : null}
        {!lead && prUrlOf(run) ? (
          <Item
            icon={<ExternalLink size={18} />}
            label="Open it on Forgejo"
            onClick={() => {
              onClose()
              window.open(prUrlOf(run), '_blank', 'noopener')
            }}
          />
        ) : null}
        {!lead && (
          <Item
            icon={<PrimeMark size={18} />}
            label="Ask Prime to look"
            hint="It reads the thread and the pull request and tells you"
            onClick={() => void askPrime()}
            testid="menu-ask-prime"
          />
        )}
        {lead && !over && (
          <Item
            icon={<Archive size={18} />}
            label={confirmFresh ? 'Yes, file away and start fresh' : 'File away and start fresh'}
            hint={
              confirmFresh
                ? 'It saves what matters to its memory, then a new session takes over'
                : 'A clean thread; what Prime remembered carries over'
            }
            tone={confirmFresh ? 'warn' : undefined}
            onClick={() => (confirmFresh ? void fresh() : setConfirmFresh(true))}
            testid="menu-fresh"
          />
        )}
        {lead && (
          <>
            <Item
              icon={<BarChart3 size={18} />}
              label="Stats"
              hint="What Prime and the agents spent and did"
              onClick={() => go('/agents/stats')}
            />
            {prime && (
              <Item
                icon={<MessageSquare size={18} />}
                label={QUICK_CHAT}
                hint="The in-app assistant behind voice and the page panel"
                onClick={() => go('/agents/$id', { id: prime.id })}
              />
            )}
          </>
        )}
        {!lead && run.spec.status === 'Failed' && (
          <Item
            icon={<RotateCcw size={18} />}
            label="Start again"
            hint="A fresh agent on the same brief and repo"
            onClick={() => {
              onClose()
              setAgain(true)
            }}
            testid="menu-again"
          />
        )}
        {working && !over && (
          <Item
            icon={<Square size={18} />}
            label="Stop this turn"
            hint="It stops where it is and waits for you"
            onClick={() => void act('interrupt', 'Stopping')}
          />
        )}
        {!over &&
          (confirmEnd ? (
            <Item
              icon={<Octagon size={18} />}
              label={lead ? 'Yes, end Prime' : 'Yes, end it'}
              hint={
                lead ? 'Start it again from the Agents screen' : 'Its sandbox goes; the pull request stays'
              }
              tone="warn"
              onClick={() => void act('end', 'Ended')}
              testid="menu-end-confirm"
            />
          ) : (
            <Item
              icon={<X size={18} />}
              label={lead ? 'End Prime' : 'End this session'}
              onClick={() => setConfirmEnd(true)}
              testid="menu-end"
            />
          ))}
      </ul>
      {again && <NewAgentSheet again={run} onClose={() => setAgain(false)} />}
    </Sheet>
  )
}
